import { useState, useMemo } from "react";
import { Calculator } from "lucide-react";
import clsx from "clsx";
import { suggestPositionSize } from "@/lib/stats";
import { formatPrice } from "@/lib/format";

interface PositionSizeCalcProps {
  accountBalance: number;
  riskPercent: number;
  currentPrice: number | null;
  /** Called when user accepts the suggestion */
  onApply: (quantity: number) => void;
}

export function PositionSizeCalc({
  accountBalance,
  riskPercent,
  currentPrice,
  onApply,
}: PositionSizeCalcProps) {
  const [slDistance, setSlDistance] = useState<string>("");

  const slDist = slDistance ? Number(slDistance) : 0;
  const qty = useMemo(
    () => suggestPositionSize(accountBalance, riskPercent, slDist),
    [accountBalance, riskPercent, slDist]
  );

  const riskDollars = (accountBalance * riskPercent) / 100;
  const positionValue = currentPrice && qty ? qty * currentPrice : 0;

  return (
    <div className="rounded-md border border-border bg-bg-elevated p-2.5">
      <div className="mb-2 flex items-center gap-1.5 text-2xs uppercase tracking-wider text-text-muted">
        <Calculator className="h-3 w-3" />
        <span>Position size calculator</span>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-2xs text-text-secondary">SL distance (points)</span>
        <input
          type="number"
          step="0.01"
          value={slDistance}
          onChange={(e) => setSlDistance(e.target.value)}
          placeholder="e.g. 5.00"
          className="rounded-md border border-border bg-bg-deep px-2 py-1 text-xs font-mono tabular text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </label>

      <div className="mt-2 grid grid-cols-2 gap-2 text-2xs">
        <Stat label="Risking" value={`$${formatPrice(riskDollars)}`} />
        <Stat label="Suggested qty" value={qty > 0 ? qty.toString() : "—"} highlight />
      </div>

      {positionValue > 0 && (
        <div className="mt-1 text-2xs text-text-muted">
          Position notional ≈ ${formatPrice(positionValue)}
        </div>
      )}

      <button
        onClick={() => qty > 0 && onApply(qty)}
        disabled={qty <= 0}
        className={clsx(
          "mt-2 w-full rounded px-2 py-1 text-2xs font-semibold transition-colors",
          qty > 0
            ? "bg-accent text-white hover:bg-accent-hover"
            : "bg-bg-hover text-text-muted cursor-not-allowed"
        )}
      >
        Apply to quantity
      </button>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex flex-col">
      <span className="text-text-muted">{label}</span>
      <span className={clsx("font-mono tabular", highlight ? "text-accent font-semibold" : "text-text-primary")}>
        {value}
      </span>
    </div>
  );
}
